import * as uuid from "uuid";
import { TgkDuplicateElaborationRequest } from "../../api/models/tgk-duplicate-elaboration-request";
import { duplicateDocumentRoot } from "../../api/document/Documents";
import PanelStandaloneManager from "../../common/panelStandaloneModal/PanelStandaloneManager";
import { buildRollForwardPermissionModal } from "./RollForwardPermission";

/**
 * Keeps the roll forward (duplicate) elaboration payloads sent to the backend available while the
 * elaboration is running, even after the panel that started it is closed, so the roll forward can be
 * re-run without copying the SharePoint user rights if the asynchronous elaboration fails because the
 * user rights could not be copied.
 *
 * The permission-warning modal is triggered from the central elaboration-handling flow
 * (see ElaborationHelper.handleNotifications) instead of the generic failure notification, so that a
 * failed roll forward is handled in a single place.
 */
class RollForwardManager {
  private readonly payloads = new Map<string, TgkDuplicateElaborationRequest>();

  storeDuplicatePayload(request: TgkDuplicateElaborationRequest): void {
    if (!request?.correlationId) return;
    this.payloads.set(request.correlationId, request);
  }

  removeDuplicatePayload(correlationId: string): void {
    this.payloads.delete(correlationId);
    PanelStandaloneManager.remove(correlationId);
  }

  hasDuplicatePayload(correlationId: string): boolean {
    return this.payloads.has(correlationId);
  }

  hasDuplicatePayloadWithUserRights(correlationId: string): boolean {
    return this.payloads.get(correlationId)?.copySharePointUserRights === true;
  }

  /**
   * Shows the permission-warning modal for the given roll forward elaboration, letting the user either
   * re-run the duplication without copying the user rights or cancel it. Returns false (and shows
   * nothing) when there is no stored payload for the correlationId, so the caller can fall back to the
   * default handling.
   */
  showPermissionWarning(correlationId: string): boolean {
    const request = this.payloads.get(correlationId);
    if (!request) return false;

    PanelStandaloneManager.addPanelStandalone({
      key: correlationId,
      ...buildRollForwardPermissionModal({
        onContinueWithoutRights: () => void this.rerunWithoutUserRights(request),
        onCancel: () => this.removeDuplicatePayload(correlationId),
      }),
    });
    return true;
  }

  private async rerunWithoutUserRights(request: TgkDuplicateElaborationRequest): Promise<void> {
    const retryRequest: TgkDuplicateElaborationRequest = {
      ...request,
      correlationId: uuid.v4(),
      copySharePointUserRights: false,
    };
    this.storeDuplicatePayload(retryRequest);
    this.removeDuplicatePayload(request.correlationId);
    try {
      await duplicateDocumentRoot(retryRequest);
    } catch (error) {
      console.error("[RollForward] Failed to re-run duplication without copying user rights", error);
      this.removeDuplicatePayload(retryRequest.correlationId);
    }
  }
}

export default new RollForwardManager();
